import { z } from 'zod'

// Reusable transformation functions
const parseNullableInt = (val: string | null): number | null =>
  val ? parseInt(val) : null
const parseStringToInt = (val: string): number => parseInt(val)

const SchoolSchema = z.object({
  id: z.number().nullable(),
  provider_id: z.number().nullable(),
  program_id: z.number().nullable(),
  school_name: z.string().nullable(),
  street_address: z.string().nullable(),
  street_address_line_2: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  state: z.string().nullable(),
  postal_zip_code: z.string().nullable(),
  is_mailing_address_same: z.union([z.string(), z.null()]).nullable(),
  mailing_street_address: z.string().nullable(),
  mailing_street_address_line_2: z.string().nullable(),
  mailing_city: z.string().nullable(),
  mailing_state: z.string().nullable(),
  mailing_zip_code: z.string().nullable(),
  school_area_location: z.string().nullable(),
  lowest_grade_served: z.string().nullable(), // Could be 'K', 'PK', '1', '2', etc.
  highest_grade_served: z.string().nullable(), // Could be '12', 'K', '8', etc.
  religious_affiliation: z.string().nullable(),
  coed_status: z.string().nullable(),
  hybrid_status: z.string().nullable(),
  areas_of_specialization: z.string().nullable(),
  learning_differences_accommodated: z.string().nullable(),
  additional_programs_offered: z.string().nullable(),
  accreditation_bodies: z.string().nullable(),
  charges_tuition: z.string().nullable(),
  pre_k_tuition: z.string().nullable(),
  kindergarten_tuition: z.string().nullable(),
  elementary_tuition: z.string().nullable(),
  middle_school_tuition: z.string().nullable(),
  high_school_tuition: z.string().nullable(),
  variable_tuition_forms: z.string().nullable(),
  per_pupil_expenditures: z.string().nullable(),
  administers_standardized_test: z.string().nullable(),
  standardized_tests_administered: z.string().nullable(),
  student_assessment_methods: z.string().nullable(),
  contracted_with_facts: z.string().nullable(),
  maximum_school_capacity: z.string().nullable(),
  current_enrollment: z.string().nullable(),
  percentage_350_poverty_guidelines: z.string().nullable(),
  additional_students_350_poverty: z.string().nullable(),
  student_teacher_ratio_full_time: z.string().nullable(),
  student_teacher_ratio_all: z.string().nullable(),
  primary_contact_first_name: z.string().nullable(),
  primary_contact_last_name: z.string().nullable(),
  primary_contact_title: z.string().nullable(),
  primary_contact_phone: z.string().nullable(),
  primary_contact_email: z.string().email().nullable(),
  primary_contact_role: z.string().nullable(),
  gender_female: z.string().nullable(),
  gender_male: z.string().nullable(),
  gender_other: z.string().nullable(),
  ethnicity_african_american: z.string().nullable(),
  ethnicity_asian: z.string().nullable(),
  ethnicity_caucasian: z.string().nullable(),
  ethnicity_east_asian: z.string().nullable(),
  ethnicity_hispanic_latino: z.string().nullable(),
  ethnicity_middle_eastern: z.string().nullable(),
  ethnicity_multiracial: z.string().nullable(),
  ethnicity_native_american: z.string().nullable(),
  ethnicity_native_hawaiian: z.string().nullable(),
  ethnicity_prefer_not_to_say: z.string().nullable(),
  percentage_esl_students: z.string().nullable(),
  percentage_esl_parents: z.string().nullable(),
  new_school_to_ace: z.string().nullable(),
  year_established: z.string().nullable(),
  history_of_school: z.string().nullable(),
  school_website: z.string().nullable(),
  online_information_source: z.string().nullable(),
  mission_statement: z.string().nullable(),
  school_picture: z.string().nullable(),
  receives_ach_payment: z.string().nullable(),
  federal_tax_id: z.string().nullable(),
  account_type: z.string().nullable(),
  depository_name: z.string().nullable(),
  bank_branch: z.string().nullable(),
  bank_street_address: z.string().nullable(),
  bank_street_address_line_2: z.string().nullable(),
  bank_city: z.string().nullable(),
  bank_state: z.string().nullable(),
  bank_routing_id: z.string().nullable(),
  account_number: z.string().nullable(),
  receive_physical_checks: z.string().nullable(),
  attestation: z.string().nullable(),
  created_at: z.string().datetime().nullable(),
  updated_at: z.string().datetime().nullable(),
})

type School = z.infer<typeof SchoolSchema>

// Refined schema with transformations
const SchoolSchemaRefined = SchoolSchema.extend({
  is_mailing_address_same: z.union([z.string(), z.null()]).transform((val) => {
    if (val === null) return null
    return val === 'Yes'
  }),
  pre_k_tuition: z.string().transform(parseNullableInt).nullable(),
  kindergarten_tuition: z.string().transform(parseNullableInt).nullable(),
  elementary_tuition: z.string().transform(parseNullableInt).nullable(),
  middle_school_tuition: z.string().transform(parseNullableInt).nullable(),
  high_school_tuition: z.string().transform(parseNullableInt).nullable(),
  per_pupil_expenditures: z.string().transform(parseNullableInt).nullable(),
  maximum_school_capacity: z.string().transform(parseStringToInt).nullable(),
  current_enrollment: z.string().transform(parseStringToInt).nullable(),
  year_established: z.string().transform(parseStringToInt).nullable(),
})

// Type for the refined schema
type SchoolRefined = z.infer<typeof SchoolSchemaRefined>

export {
  SchoolSchema,
  SchoolSchemaRefined,
  type School,
  type SchoolRefined,
  parseNullableInt,
  parseStringToInt,
}

export const SchoolResponseSchema = SchoolSchema.omit({
  primary_contact_email: true,
  primary_contact_phone: true,
  primary_contact_first_name: true,
  primary_contact_last_name: true,
  primary_contact_role: true,
  primary_contact_title: true,
  mailing_city: true,
  mailing_state: true,
  mailing_zip_code: true,
  mailing_street_address: true,
  mailing_street_address_line_2: true,
  is_mailing_address_same: true,
})

export type SchoolResponseSchema = z.infer<typeof SchoolResponseSchema>
